import React, { useCallback, useEffect, lazy, Suspense } from 'react';
import { Link, Route, Switch, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Hub } from '@aws-amplify/core';
import Wallet from '@kambria/kambria-wallet';
import { BlockchainAlert, FollowUs, Header, Nav, NavItem, PageLoader } from '@kambria/kambria-lego';
import Footer from './views/components/Footer';
import Error404 from './views/errors/404';
import NavProfile from 'views/components/core/NavProfile';
import CircleLoader from 'views/components/core/CircleLoader';
import { fetchUser, linkProvider } from 'redux/actions/user.action';
import { connectWallet, watchInfo } from 'redux/actions/blockchain.action';
import { getKarma } from 'redux/actions/karma.action';
import { getCredits } from 'redux/actions/credit.action';
import Utils from 'helpers/utils.js';
import config from 'config';
import routes from 'routes';
import '@kambria/kambria-lego/build/css/index.css';
import 'static/styles/index.css';
import 'antd/dist/antd.css';
import logo from 'logo.svg';
import discordIcon from 'static/images/footer/discord.png';
import facebookIcon from 'static/images/footer/facebook.png';
import twitterIcon from 'static/images/footer/twitter.png';
import linkedinIcon from 'static/images/footer/linkedin.png';
import youtubeIcon from 'static/images/footer/youtube.png';

const followUs = [
  { name: 'Discord', url: 'https://discord.gg/DM333x2M55', icon: discordIcon },
  { name: 'Facebook', url: 'https://www.facebook.com/KambriaNetwork', icon: facebookIcon },
  { name: 'Twitter', url: 'https://twitter.com/KambriaNetwork', icon: twitterIcon },
  { name: 'Linkedin', url: 'https://www.linkedin.com/company/kambria/', icon: linkedinIcon },
  { name: 'Youtube', url: 'https://www.youtube.com/channel/UCSVHcYVKbZQ4DRzuCItarrQ/featured', icon: youtubeIcon },
];

const navList = [
  { url: '/', title: 'Platform', exact: true },
  { url: '/bounty', title: 'Bounty', exact: true },
  // { url: '/gif/researchfunding', title: 'GIF', exact: true },

  { url: '/token-sale', title: 'Token Sale', exact: true },
  { url: '/code-base', title: 'Codebase', exact: true },
  { url: '/teams', title: 'Teams', active: true },
  { url: 'https://edukation.kambria.io/', title: 'EduKATion', exact: false },
];

const walletOptions = {
  networkId: { ethereum: config.eth.NETWORK },
  pageRefreshing: true,
};

function HeaderApp({ blockchain, ui, user, history, connectWallet }) {
  const clickStyle = { cursor: 'pointer', textDecoration: 'underline' };
  const onConnect = () => connectWallet(true);
  return (
    <section className="App__header">
      <Header logo={{ image: logo, url: 'https://kambria.io' }}>
        <Nav>
          {navList.map((item, index) => (
            <NavItem key={index} item={item} />
          ))}
          {user.loading ? (
            <li>
              <CircleLoader style={{ marginTop: 12 }} />
            </li>
          ) : user.authenticated ? (
            <NavProfile history={history} blockchain={blockchain} />
          ) : (
            <li>
              <Link className="btn btn-secondary btn-sm btn-login" to="/auth">
                Login / Register
              </Link>
            </li>
          )}
        </Nav>
        {ui.status === 'loading' && <PageLoader type="top" />}
      </Header>
    </section>
  );
}

config.init();

function App({ blockchain, user, ui, history, fetchUser, getKarma, linkProvider, connectWallet, watchInfo, getCredits }) {
  const getCurrentUser = useCallback(
    () => fetchUser((user) => {
      getKarma(user.email);
      getCredits(user.email);
    }),
    [fetchUser, getKarma, getCredits]
  );

  const saveUTMTracking = useCallback(() => {
    const params = Utils.parseQueryString(history.location.search);
    const utmParams = {};
    Object.keys(params).forEach((key) => {
      if (key.includes('utm')) {
        const newKey = Utils.toCamel(key);
        utmParams[newKey] = params[key];
      }
    });
    if (Object.keys(utmParams).length) {
      const location = window.location;
      utmParams.url = location.protocol + '//' + location.host + location.pathname;
      utmParams.href = location.href;
      localStorage.setItem('utmParams', JSON.stringify(utmParams));
    }
  }, [history]);

  const listenAuth = useCallback(() => {
    const cognitoAuth = window.cognitoAuth;
    if (cognitoAuth) {
      const listener = (args) => {
        const { event, data } = args.payload;
        switch (event) {
          case 'signIn':
            getCurrentUser();
            break;
          case 'federatedSignIn':
            linkProvider(
              {
                authorization: data.accessToken,
                provider: data.provider,
              },
              () => {
                cognitoAuth.IdentityProvider = data.provider;
                cognitoAuth.getSession();
              },
            );
            break;
          case 'returnSession':
            history.replace(localStorage.getItem('pathBeforeSignIn') || '');
            break;
          case 'federatedSignInFailure':
          case 'signUp':
          case 'signOut':
          case 'signIn_failure':
          case 'configured':
          default:
            break;
        }
      };
      Hub.listen('auth', listener);
      const curUrl = window.location.href;
      cognitoAuth.parseCognitoWebResponse(curUrl);
    }
  }, [getCurrentUser, linkProvider, history]);

  const doneWallet = (er) => {
    connectWallet(false);
    if (er) return console.error(er);
    return watchInfo();
  };

  useEffect(() => {
    saveUTMTracking();
    listenAuth();
    getCurrentUser();
    document.getElementsByTagName('body')[0].style.height = '100%';
  }, [saveUTMTracking, listenAuth, getCurrentUser]);

  const headerProps = { blockchain, user, ui, connectWallet };
  const commonProps = {
    header: <HeaderApp {...headerProps} />,
    footer: (
      <Footer>
        <FollowUs data={followUs} />
      </Footer>
    ),
  };

  return (
    <>
      <Suspense fallback={<div>Loading...</div>}>
        <Switch>
          {routes.map((route) => (
            <Route
              exact
              key={route.path}
              path={route.path}
              render={(props) => <route.component {...commonProps} {...props} />}
            />
          ))}
          <Route render={(props) => <Error404 {...commonProps} {...props} />} />
        </Switch>
      </Suspense>
      <Wallet visible={blockchain.visible} options={walletOptions} done={doneWallet} />
    </>
  );
}

const mapStateToProps = ({ blockchain, ui, user }) => ({ blockchain, ui, user });
const mapDispatchToProps = { fetchUser, getKarma, linkProvider, connectWallet, watchInfo, getCredits };

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
